import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';

import {and, buildQueryString} from '@edna/utils';

import {TData, TPayload} from 'src/components/List';
import {ESortDirection, TChannelType, TItemId} from 'src/constants';
import {EPermission} from 'src/containers/User';
import {CACHE_LIFETIME, ECacheTag, rootApi} from 'src/models';
import {ISO3CountryCodeList, combineDateAndTime, getAbsoluteDateTime} from 'src/utils';

import {
  MOSCOW_TIMEZONE_OFFSET,
  TCatalogTariffsFilters,
  TDetailFormData,
  TTariff,
  TTariffCopyPayload,
  TTariffDetail,
  TTariffDetailFullInfo,
  TTariffDetailsGroupedSearchInfo,
  TTariffFilters,
  TTariffPricingDetail,
} from './definitions';

const prepareActiveFromDate = (
  activeFromDate: TDetailFormData['activeFromDate'],
  activeFromTime: TDetailFormData['activeFromTime'],
) => {
  const absoluteDate = getAbsoluteDateTime(combineDateAndTime(activeFromDate, activeFromTime));

  return new Date(`${absoluteDate}${MOSCOW_TIMEZONE_OFFSET}`);
};

const prepareDetailForSave = (values: TDetailFormData) => {
  const {activeFromDate, activeFromTime, data, ...formValues} = values;
  let jsonData = null;

  try {
    jsonData = JSON.parse(data);
  } catch (e) {}

  return {
    ...formValues,
    data: jsonData,
    activeFrom: prepareActiveFromDate(activeFromDate, activeFromTime),
  };
};

const groupPricingDetails = (details: TTariffPricingDetail[]) => {
  return Object.values(groupBy(details, (detail) => detail.id));
};

const transformDetails = <T extends TTariffDetailFullInfo | TTariffDetail | TTariffPricingDetail>(
  details: T[][],
) => {
  const transformedDetails = details.reduce((result, detailsById) => {
    const {active, planned, archived} = detailsById.reduce(
      (detailsByStatus, detail) => {
        const resultDetail: T =
          'pricingDetails' in detail
            ? {
                ...detail,
                pricingDetails: transformDetails(groupPricingDetails(detail.pricingDetails)),
              }
            : detail;

        if (resultDetail.active) {
          return {...detailsByStatus, active: resultDetail};
        }

        if (resultDetail.planned) {
          return {...detailsByStatus, planned: resultDetail};
        }

        return {...detailsByStatus, archived: [...detailsByStatus.archived, resultDetail]};
      },
      {active: null, planned: null, archived: []} as {
        active: T | null;
        planned: T | null;
        archived: T[];
      },
    );

    const archivedSorted =
      archived.length > 1 ? archived.sort((a, b) => b.version - a.version) : archived;

    if (active) {
      return [...result, {...active, plannedDetail: planned, archivedDetails: archivedSorted}];
    }

    if (planned) {
      return [...result, {...planned, archivedDetails: archivedSorted}];
    }

    return [
      ...result,
      {
        ...archivedSorted?.[0],
        archivedDetails: archivedSorted.slice(1),
      },
    ];
  }, [] as T[]);

  return transformedDetails;
};

const transformPricingDetails = (details: TTariffPricingDetail[]) => {
  return transformDetails(groupPricingDetails(details));
};

const filterCatalogTariffs = (data: TTariff[] = [], filters: TCatalogTariffsFilters = {}) => {
  const filtered = data.filter(
    (tariff) => !filters.filter || tariff.name.toLowerCase().includes(filters.filter.toLowerCase()),
  );
  const sorted = sortBy(filtered, filters.sort?.property ?? 'id');

  return filters.sort?.direction === ESortDirection.DESC ? sorted.reverse() : sorted;
};

const tariffsApi = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getCatalogTariffs: builder.query<TTariff[], TCatalogTariffsFilters | void>({
      query: () => ({
        method: 'GET',
        url: '/billing/tariffs/all',
        meta: {
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ'),
        },
      }),
      transformResponse: (data: TTariff[], _, payload: TCatalogTariffsFilters) =>
        filterCatalogTariffs(data, payload),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.TARIFFS],
    }),
    getTariff: builder.query<TTariff, TItemId>({
      query: (tariffId) => ({
        method: 'GET',
        url: `/billing/tariffs/${tariffId}`,
        meta: {
          isShowError: true,
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ'),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.TARIFFS],
    }),
    saveTariff: builder.mutation<TTariff, TTariff & {tariffId?: number}>({
      query: (data) => ({
        method: data.id ? 'PUT' : 'POST',
        data,
        url: `/billing/tariffs`,
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToSaveItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.saveSuccess',
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
        },
      }),
      invalidatesTags: (_, __, {tariffId}) => [{type: ECacheTag.TARIFFS, id: tariffId}],
    }),
    getTariffDetails: builder.query<
      TData<TTariffDetailsGroupedSearchInfo> | undefined,
      TPayload<{tariffId: TItemId}>
    >({
      query: ({tariffId, ...data}) => ({
        method: 'POST',
        url: `/billing/tariffs/${tariffId}/details`,
        data,
        meta: {
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ'),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.TARIFFS_DETAILS],
      transformResponse: (data: TData<TTariffDetail[]>) =>
        data
          ? ({
              last: data.last,
              content: transformDetails(data.content),
            } as TData<TTariffDetailsGroupedSearchInfo>)
          : undefined,
    }),
    searchTariffDetails: builder.query<
      TData<TTariffDetailsGroupedSearchInfo> | undefined,
      TPayload<TTariffFilters & {onlyActive?: boolean; tariffId: TItemId}>
    >({
      query: ({limit, offset, sort, tariffId, ...params}) => ({
        method: 'POST',
        data: {limit, offset},
        url: `/billing/tariffs/${tariffId}/details/search${buildQueryString(params)}`,
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: [ECacheTag.TARIFFS_DETAILS],
      transformResponse: (data: TData<TTariffDetail[]>) =>
        data
          ? ({
              last: data.last,
              content: transformDetails(data.content),
            } as TData<TTariffDetailsGroupedSearchInfo>)
          : undefined,
    }),
    getTariffDetailsFilters: builder.query<TTariffFilters, TItemId>({
      query: (tariffId) => ({
        method: 'GET',
        url: '/billing/tariffs/search/filters-for-tariff',
        params: {tariffId},
        meta: {
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ'),
        },
      }),
      providesTags: [ECacheTag.TARIFFS_DETAILS, ECacheTag.TARIFFS_PRICING_DETAILS],
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    getTariffDetailVersions: builder.query<TTariffDetail[], TItemId>({
      query: (detailId) => ({
        method: 'GET',
        url: `/billing/tariffs/details/all-by-id/${detailId}`,
        meta: {
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ'),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (_, __, id) => [{id, type: ECacheTag.TARIFFS_DETAILS}],
    }),
    getTariffDetailOperators: builder.query<{operator: string; country: string}[], void>({
      query: () => ({
        method: 'GET',
        url: '/operator/list',
      }),
      transformResponse: (items: {operator: string; country: string}[] = []) =>
        items.filter((item) => ISO3CountryCodeList.includes(item.country)),
      keepUnusedDataFor: CACHE_LIFETIME,
    }),
    saveTariffDetail: builder.mutation<TTariffDetail, TDetailFormData & {catalogTariffId: number}>({
      query: (data) => ({
        method: data.id ? 'PUT' : 'POST',
        data: prepareDetailForSave(data),
        url: `/billing/tariffs/details`,
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToSaveItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.saveSuccess',
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_DETAILS],
    }),
    archiveTariffDetail: builder.mutation<void, {id: number; version: number}>({
      query: (data) => ({
        method: 'DELETE',
        data,
        url: `/billing/tariffs/details/soft-delete`,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'TariffsCatalog:cancelChangesModal.successNotification',
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_DETAILS],
    }),
    getTariffPricingDetails: builder.query<TTariffPricingDetail[], TItemId>({
      query: (detailId) => ({
        method: 'GET',
        url: `/billing/tariffs/details/${detailId}/prices`,
        meta: {
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ'),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      transformResponse: transformPricingDetails,
      providesTags: [ECacheTag.TARIFFS_PRICING_DETAILS],
    }),
    getTariffPricingDetailVersions: builder.query<TTariffPricingDetail[], TItemId>({
      query: (pricingId) => ({
        method: 'GET',
        url: `/billing/tariffs/details/prices/${pricingId}`,
        meta: {
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ'),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (_, __, id) => [{id, type: ECacheTag.TARIFFS_PRICING_DETAILS}],
    }),
    saveTariffPricingDetail: builder.mutation<
      TTariffPricingDetail,
      TDetailFormData & {tariffDetailId: number}
    >({
      query: (data) => ({
        method: data.id ? 'PUT' : 'POST',
        data: prepareDetailForSave(data),
        url: `/billing/tariffs/details/prices`,
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToSaveItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.saveSuccess',
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_DETAILS, ECacheTag.TARIFFS_PRICING_DETAILS],
    }),
    archiveTariffPricingDetail: builder.mutation<void, {id: number; version: number}>({
      query: (data) => ({
        method: 'DELETE',
        data,
        url: `/billing/tariffs/details/prices/soft-delete`,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'TariffsCatalog:cancelChangesModal.successNotification',
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_DETAILS, ECacheTag.TARIFFS_PRICING_DETAILS],
    }),
    copyTariff: builder.mutation<TTariff, TTariffCopyPayload>({
      query: ({activeFromTime, activeFromDate, ...data}) => ({
        method: 'POST',
        data: {
          ...data,
          activeFrom: prepareActiveFromDate(activeFromDate, activeFromTime),
        },
        url: '/billing/tariffs/copy',
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToSaveItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.saveSuccess',
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS],
    }),
    deleteTariff: builder.mutation<void, TItemId>({
      query: (tariffId) => ({
        method: 'DELETE',
        data: {tariffId},
        url: '/billing/tariffs/hard-delete',
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToDeleteItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.deleteSuccess',
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS],
    }),
    uploadTariff: builder.mutation<
      void,
      {channelType: TChannelType; catalogTariffId: TItemId; activeFrom: Date; file: File}
    >({
      query: ({file, ...request}) => {
        const formData = new FormData();

        formData.append('request', new Blob([JSON.stringify(request)], {type: 'application/json'}));
        formData.append('file', file);

        return {
          method: 'POST',
          url: '/billing/tariffs/updater/regions/csv',
          data: formData,
          meta: {
            isShowError: true,
            isShowSuccess: true,
            successMessageKey: 'List:notification.saveSuccess',
            permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
          },
        };
      },
      invalidatesTags: [ECacheTag.TARIFFS, ECacheTag.TARIFFS_DETAILS],
    }),
    cancelTariffPlannedChanges: builder.mutation<void, number>({
      query: (catalogTariffId) => ({
        method: 'POST',
        data: {catalogTariffId},
        url: '/billing/tariffs/updater/regions/cancel-planned-prices',
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'TariffsCatalog:tariffCard.cancelPlannedChangesSuccess',
          permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
        },
      }),
      invalidatesTags: [ECacheTag.TARIFFS_DETAILS, ECacheTag.TARIFFS_PRICING_DETAILS],
    }),
  }),
});

export const {
  useGetCatalogTariffsQuery,
  useGetTariffQuery,
  useSaveTariffMutation,
  useCopyTariffMutation,
  useDeleteTariffMutation,
  useSearchTariffDetailsQuery,
  useGetTariffDetailsFiltersQuery,

  useGetTariffDetailsQuery,
  useGetTariffDetailVersionsQuery,
  useGetTariffDetailOperatorsQuery,
  useSaveTariffDetailMutation,
  useArchiveTariffDetailMutation,

  useGetTariffPricingDetailsQuery,
  useGetTariffPricingDetailVersionsQuery,
  useSaveTariffPricingDetailMutation,
  useArchiveTariffPricingDetailMutation,
  useUploadTariffMutation,

  useCancelTariffPlannedChangesMutation,
} = tariffsApi;
